export interface ErrorContext {
  functionName: string;
  parameters?: Record<string, any>;
  timestamp: Date;
  userId?: string;
  roomId?: string;
}

export class AppError extends Error {
  public readonly code: string;
  public readonly context: ErrorContext;
  public readonly isOperational: boolean;

  constructor(
    message: string,
    code: string,
    context: ErrorContext,
    isOperational: boolean = true
  ) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.context = context;
    this.isOperational = isOperational;
    
    // Maintains proper stack trace for where our error was thrown
    Error.captureStackTrace(this, AppError);
  }
}

export const ErrorCodes = {
  // Authentication errors
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  AUTH_INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  AUTH_INSUFFICIENT_PERMISSIONS: 'AUTH_INSUFFICIENT_PERMISSIONS',
  AUTH_TOKEN_EXPIRED: 'AUTH_TOKEN_EXPIRED',
  AUTH_EMAIL_NOT_VERIFIED: 'AUTH_EMAIL_NOT_VERIFIED',
  
  // Firestore errors
  FIRESTORE_PERMISSION_DENIED: 'FIRESTORE_PERMISSION_DENIED',
  FIRESTORE_NOT_FOUND: 'FIRESTORE_NOT_FOUND',
  FIRESTORE_INVALID_DATA: 'FIRESTORE_INVALID_DATA',
  FIRESTORE_NETWORK_ERROR: 'FIRESTORE_NETWORK_ERROR',
  
  // Validation errors
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  VALIDATION_REQUIRED_FIELD: 'VALIDATION_REQUIRED_FIELD',
  VALIDATION_INVALID_FORMAT: 'VALIDATION_INVALID_FORMAT',
  
  // AI errors
  AI_PARSE_FAILED: 'AI_PARSE_FAILED',
  AI_LOW_CONFIDENCE: 'AI_LOW_CONFIDENCE',
  AI_SERVICE_UNAVAILABLE: 'AI_SERVICE_UNAVAILABLE',
  
  // Business logic errors
  WAITLIST_FULL: 'WAITLIST_FULL',
  TABLE_FULL: 'TABLE_FULL',
  INVALID_TABLE_STATUS: 'INVALID_TABLE_STATUS',
  ROOM_ACCESS_DENIED: 'ROOM_ACCESS_DENIED',
  
  // Network errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  
  // Unknown errors
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

export type ErrorCode = typeof ErrorCodes[keyof typeof ErrorCodes];

// Error handler function
export const handleError = (
  error: unknown,
  context: Omit<ErrorContext, 'timestamp'>
): AppError => {
  const errorContext: ErrorContext = {
    ...context,
    timestamp: new Date(),
  };

  // If it's already our custom error, just add context
  if (error instanceof AppError) {
    return new AppError(
      error.message,
      error.code,
      { ...errorContext, ...error.context },
      error.isOperational
    );
  }

  // Handle Firebase errors
  if (error && typeof error === 'object' && 'code' in error) {
    const firebaseError = error as any;
    
    switch (firebaseError.code) {
      // Firebase Auth errors
      case 'auth/invalid-credential':
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return new AppError(
          'Invalid email or password',
          ErrorCodes.AUTH_INVALID_CREDENTIALS,
          errorContext
        );
      case 'auth/user-disabled':
        return new AppError(
          'This account has been disabled',
          ErrorCodes.AUTH_INVALID_CREDENTIALS,
          errorContext
        );
      case 'auth/too-many-requests':
        return new AppError(
          'Too many failed attempts. Please try again later',
          ErrorCodes.AUTH_INVALID_CREDENTIALS,
          errorContext
        );
      case 'auth/network-request-failed':
        return new AppError(
          'Network error. Please check your connection',
          ErrorCodes.NETWORK_ERROR,
          errorContext
        );
      case 'auth/invalid-email':
        return new AppError(
          'Invalid email format',
          ErrorCodes.VALIDATION_INVALID_FORMAT,
          errorContext
        );
      case 'auth/weak-password':
        return new AppError(
          'Password is too weak',
          ErrorCodes.VALIDATION_FAILED,
          errorContext
        );
      case 'auth/email-already-in-use':
        return new AppError(
          'An account with this email already exists',
          ErrorCodes.VALIDATION_FAILED,
          errorContext
        );
      
      // Firestore errors
      case 'permission-denied':
        return new AppError(
          'You do not have permission to perform this action',
          ErrorCodes.FIRESTORE_PERMISSION_DENIED,
          errorContext
        );
      case 'not-found':
        return new AppError(
          'The requested resource was not found',
          ErrorCodes.FIRESTORE_NOT_FOUND,
          errorContext
        );
      case 'invalid-argument':
        return new AppError(
          'Invalid data provided',
          ErrorCodes.FIRESTORE_INVALID_DATA,
          errorContext
        );
      case 'unavailable':
        return new AppError(
          'Service temporarily unavailable',
          ErrorCodes.FIRESTORE_NETWORK_ERROR,
          errorContext
        );
      default:
        return new AppError(
          'An error occurred. Please try again',
          ErrorCodes.UNKNOWN_ERROR,
          errorContext
        );
    }
  }

  // Handle network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return new AppError(
      'Network error occurred. Please check your connection.',
      ErrorCodes.NETWORK_ERROR,
      errorContext
    );
  }

  // Handle validation errors (Zod)
  if (error && typeof error === 'object' && 'issues' in error) {
    const zodError = error as any;
    const message = zodError.issues?.map((issue: any) => issue.message).join(', ') || 'Validation failed';
    
    // Don't log detailed validation errors to console in production
    
    return new AppError(
      message,
      ErrorCodes.VALIDATION_FAILED,
      errorContext
    );
  }

  // Handle generic errors
  const message = error instanceof Error ? error.message : 'An unknown error occurred';
  return new AppError(
    message,
    ErrorCodes.UNKNOWN_ERROR,
    errorContext,
    false // Not operational
  );
};

// Error logging utility
export const logError = (error: AppError, additionalContext?: Record<string, any>) => {
  // Don't log sensitive validation details
  const sanitizedContext = { ...error.context };
  if (sanitizedContext.parameters) {
    // Remove sensitive fields from parameters
    const { password, ...safeParams } = sanitizedContext.parameters;
    sanitizedContext.parameters = safeParams;
  }

  const logData = {
    message: error.message,
    code: error.code,
    context: sanitizedContext,
    stack: error.stack,
    additionalContext,
  };

  // In development, log to console but be careful with sensitive data
  if (process.env.NODE_ENV === 'development') {
    console.error('AppError:', logData);
  }

  // In production, you might want to send to a logging service
  // e.g., Sentry, LogRocket, etc.
  if (process.env.NODE_ENV === 'production') {
    // Production logging service integration can be added here
    // For now, errors are logged to console for monitoring
  }
};

// User-friendly error messages
export const getUserFriendlyMessage = (error: AppError): string => {
  switch (error.code) {
    case ErrorCodes.AUTH_REQUIRED:
      return 'Please log in to continue';
    case ErrorCodes.AUTH_INVALID_CREDENTIALS:
      return 'Invalid email or password';
    case ErrorCodes.AUTH_INSUFFICIENT_PERMISSIONS:
      return 'You do not have permission to perform this action';
    case ErrorCodes.AUTH_EMAIL_NOT_VERIFIED:
      return 'Please verify your email address before signing in. A verification link has been sent to your email.';
    case ErrorCodes.FIRESTORE_PERMISSION_DENIED:
      return 'Access denied. Please check your permissions';
    case ErrorCodes.FIRESTORE_NOT_FOUND:
      return 'The requested item was not found';
    case ErrorCodes.VALIDATION_FAILED:
      return 'Please check your input and try again';
    case ErrorCodes.WAITLIST_FULL:
      return 'This table\'s waitlist is full';
    case ErrorCodes.TABLE_FULL:
      return 'This table is full';
    case ErrorCodes.AI_PARSE_FAILED:
      return 'Could not understand your request. Please try rephrasing';
    case ErrorCodes.AI_LOW_CONFIDENCE:
      return 'Your request was unclear. Please provide more details';
    case ErrorCodes.NETWORK_ERROR:
      return 'Network error. Please check your connection and try again';
    default:
      return 'Something went wrong. Please try again';
  }
};

// Async error wrapper
export const withErrorHandling = <T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  context: Omit<ErrorContext, 'timestamp' | 'parameters'>
) => {
  return async (...args: T): Promise<R> => {
    try {
      return await fn(...args);
    } catch (error) {
      const appError = handleError(error, {
        ...context,
        parameters: args.length > 0 ? { args } : {},
      });
      logError(appError);
      throw appError;
    }
  };
};
